import * as React from 'react';

import { concatStyleSets, IButtonStyles, ITheme, memoizeFunction } from '@fluentui/react';

import { AppButton, getActionButtonStyles } from '@1js/acui-button';
// eslint-disable-next-line no-restricted-imports
import { useNextRibbonButtonEnabled } from '@1js/acui-button/lib/Next';
import { getModernUxExperimentPreference } from '@1js/acui-common';
import { AppThemeConsumer } from '@1js/acui-theme';

import { getModernUxRibbonButtonStyles } from '../../../../styling/modernUxStyles';
import { animatePropsChanges } from '../../components/PropsAnimator/PropsAnimator';
import { getOnRenderText } from '../onRenderText';
import { RibbonButton as RibbonButtonNext } from './next';
import { RibbonButtonInternalProps } from './RibbonButton.props';
import { getRibbonButtonLabelTransitionStyles } from './RibbonButton.styles';
import { getHideLabelAnimationOptions } from './RibbonButtonHideLabelAnimation';

const getStyles = memoizeFunction(
  (
    actionButtonStyles?: IButtonStyles,
    transitionStyles?: IButtonStyles,
    customStyles?: IButtonStyles,
    modernUxButtonStyles?: IButtonStyles
  ) => {
    if (getModernUxExperimentPreference()) {
      return concatStyleSets(actionButtonStyles, transitionStyles, modernUxButtonStyles, customStyles);
    }
    return concatStyleSets(actionButtonStyles, transitionStyles, customStyles);
  }
);

class BaseRibbonButton extends React.PureComponent<RibbonButtonInternalProps, {}> {
  private appButton = React.createRef<AppButton>();

  public render() {
    const transitionStyles = getRibbonButtonLabelTransitionStyles(
      this.props.textContainerInitialWidth,
      this.props.textContainerFinalWidth,
      this.props.hideLabel
    );

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <AppButton
            {...this.props}
            ref={this.appButton}
            styles={getStyles(
              getActionButtonStyles(theme),
              transitionStyles,
              this.props.styles,
              getModernUxRibbonButtonStyles()
            )}
            onRenderText={this.props.onRenderText || getOnRenderText(this.props.size)}
            dataAutomationType={this.props.dataAutomationType || 'RibbonButton'}
          />
        )}
      </AppThemeConsumer>
    );
  }
  public focus() {
    this.appButton && this.appButton.current && this.appButton.current.focus();
  }
}

const RibbonButtonCurrent = animatePropsChanges(getHideLabelAnimationOptions<RibbonButtonInternalProps>())(
  BaseRibbonButton
);

RibbonButtonCurrent.displayName = 'RibbonButtonCurrent';

export const RibbonButton = React.forwardRef<HTMLButtonElement, RibbonButtonInternalProps>((props, ref) => {
  const isNewButtonEnabled = useNextRibbonButtonEnabled() && !props.useLegacyButton;
  return isNewButtonEnabled ? (
    <RibbonButtonNext ref={ref} {...props} />
  ) : (
    <RibbonButtonCurrent ref={ref as any} {...props} />
  );
});

RibbonButton.displayName = 'RibbonButton';

export type RibbonButton = typeof RibbonButton.prototype;
